/** Minimal shape shared by `Question`, `FormTreeQuestion`, and RogerDoc tree rows. */
export type QuestionTreeNode = {
  qid: string;
  subitems?: Record<string, QuestionTreeNode[]>;
};

export type WalkQuestionTreeContext = {
  /** `0` for direct `section_content` rows. */
  depth: number;
  /** Immediate parent qid, or `null` at depth 0. */
  parentQid: string | null;
  /** Subitem answer key under the parent, or `null` at depth 0. */
  parentAnswerKey: string | null;
  /** True for direct `section_content` entries. */
  isTopLevel: boolean;
};

/**
 * Immediate schema parent for a nested subitem QID.
 *
 * QSB may place the same child under several answer keys of **one** parent
 * question (multi-homed). `answerKeys` lists every such key. Homing under
 * different parent questions is unsupported — later parents are ignored.
 */
export type QuestionTreeParentInfo = {
  parentQid: string;
  answerKeys: string[];
};

export type QuestionTreeHierarchy = {
  /** QIDs that appear directly in the section's `section_content`. */
  topLevelQids: Set<string>;
  /** Immediate schema parent for each nested subitem QID. */
  parentOf: Map<string, QuestionTreeParentInfo>;
};

/**
 * Depth-first walk of a Roger question tree (`section_content` + nested `subitems`).
 */
export function walkQuestionTree<T extends QuestionTreeNode>(
  questions: T[],
  visit: (question: T, ctx: WalkQuestionTreeContext) => void
): void {
  const walkSubitems = (
    subitems: Record<string, T[]>,
    parentQid: string,
    depth: number
  ) => {
    for (const [answerKey, rows] of Object.entries(subitems)) {
      for (const row of rows ?? []) {
        visit(row, {
          depth,
          parentQid,
          parentAnswerKey: answerKey,
          isTopLevel: false,
        });
        if (row.subitems && Object.keys(row.subitems).length > 0) {
          walkSubitems(row.subitems as Record<string, T[]>, row.qid, depth + 1);
        }
      }
    }
  };

  for (const row of questions) {
    visit(row, {
      depth: 0,
      parentQid: null,
      parentAnswerKey: null,
      isTopLevel: true,
    });
    if (row.subitems && Object.keys(row.subitems).length > 0) {
      walkSubitems(row.subitems as Record<string, T[]>, row.qid, 1);
    }
  }
}

/** Every schema QID in the section subtree, including nested `subitems`. */
export function collectQuestionTreeQids<T extends QuestionTreeNode>(
  questions: T[]
): string[] {
  const qids: string[] = [];
  walkQuestionTree(questions, q => {
    qids.push(q.qid);
  });
  return qids;
}

/** Pre-order flatten of every question row in the section subtree. */
export function flattenQuestionTree<T extends QuestionTreeNode>(
  questions: T[]
): T[] {
  const out: T[] = [];
  walkQuestionTree(questions, q => {
    out.push(q);
  });
  return out;
}

/** QID → question row lookup for the full section subtree. */
export function buildQuestionTreeMap<T extends QuestionTreeNode>(
  questions: T[]
): Map<string, T> {
  const map = new Map<string, T>();
  walkQuestionTree(questions, q => {
    map.set(q.qid, q);
  });
  return map;
}

export function buildQuestionTreeHierarchy<T extends QuestionTreeNode>(
  questions: T[]
): QuestionTreeHierarchy {
  const topLevelQids = new Set<string>();
  const parentOf = new Map<string, QuestionTreeParentInfo>();

  walkQuestionTree(questions, (q, ctx) => {
    if (ctx.isTopLevel) {
      topLevelQids.add(q.qid);
      return;
    }
    if (ctx.parentQid == null || ctx.parentAnswerKey == null) return;

    const existing = parentOf.get(q.qid);
    if (existing == null) {
      parentOf.set(q.qid, {
        parentQid: ctx.parentQid,
        answerKeys: [ctx.parentAnswerKey],
      });
      return;
    }

    // Same parent question: union answer keys (QSB multi-home).
    if (existing.parentQid === ctx.parentQid) {
      if (!existing.answerKeys.includes(ctx.parentAnswerKey)) {
        existing.answerKeys.push(ctx.parentAnswerKey);
      }
      return;
    }

    // Different parent question: unsupported — keep the first parent.
  });

  for (const info of parentOf.values()) {
    info.answerKeys.sort((a, b) => a.localeCompare(b));
  }

  return { topLevelQids, parentOf };
}
